// ProtectedRoute — real RBAC gate (CHG-1289 / ITER-3414).
//
// Closes the CHG-1225 "stub-always-permits" forward-promise. The stub
// previously rendered children unconditionally for any authenticated
// user; now we:
//   1. Wait for the useAuth() loading + usePermissions() loading state.
//   2. If not authenticated → redirect to "/login?returnTo=<path>"
//      (CHG-1461 / ITER-3577 — was "/" pre-real-auth; flipped to /login
//      now that v2 owns its own login surface).
//   3. If authenticated but lacks `requirePermission` → render
//      <AccessDeniedPage missingPermission={...} /> instead of children.
//   4. Otherwise → render children.
//
// FE check is a UX optimization. The BE require_permission dependency
// (per route) is the authority; a stale-permission user who reaches an
// endpoint they can't access gets 403, handled by the api/client
// interceptor.
//
// Same props API as the stub (`requirePermission: string`). No App.tsx
// callsite changes needed.

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useHasPermission, usePermissions } from "../auth/usePermissions";
import { AccessDeniedPage } from "../pages/AccessDenied";

export interface ProtectedRouteProps {
  children: ReactNode;
  /** Permission code required to view this route. Empty/undefined = no
   * permission required (rare; most routes specify a code). */
  requirePermission?: string;
}

export function ProtectedRoute({ children, requirePermission }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: permsLoading, isError: permsError } = usePermissions();
  const hasPermission = useHasPermission(requirePermission);
  const location = useLocation();

  if (authLoading) return null;

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // Permissions still loading — render a small skeleton so the route
  // mount doesn't flash content the user might not have permission to
  // see. Returning null is acceptable for a sub-second wait; the
  // skeleton makes the gating visible.
  if (permsLoading) {
    return (
      <div
        data-protected-route-loading="true"
        style={{ padding: 24, color: "var(--color-text-muted)", fontSize: 13 }}
      >
        Checking permissions…
      </div>
    );
  }

  // If permissions failed to load + a permission is required → fail closed.
  // (Treating as access-denied lets the user retry via reload; the
  // alternative would be re-fetch-on-error which can loop on auth issues.)
  if (permsError && requirePermission) {
    return <AccessDeniedPage missingPermission={requirePermission} />;
  }

  if (requirePermission && !hasPermission) {
    return <AccessDeniedPage missingPermission={requirePermission} />;
  }

  return <>{children}</>;
}
